/**
 * Form inputs and the inline controls that sit beside them.
 *
 * Validation messages render adjacent to the field, which is why every input
 * takes an `error` string rather than a boolean: the message is part of the
 * component's contract with the user.
 *
 * Ported from the previous `@yummy/ui` kit component for component — `Field`,
 * `TextField`, `SwitchField`, `ChoiceField`, `TimeField`, and `DateField` from
 * its text-field module, `SegmentedControl` and `Stepper` from its overlay
 * module. The exported names, props, optionality, and defaults are unchanged so
 * screens port mechanically and a missing prop is a compile error rather than a
 * silent behaviour change. The implementation is React Native Reusables
 * primitives styled with `className`.
 */

import { forwardRef, useId, useLayoutEffect, useRef, useState, type ComponentRef, type ReactNode } from 'react';
import { CalendarDays, Clock } from 'lucide-react-native';
import {
  Pressable,
  ScrollView,
  TextInput,
  View,
  type TextInputProps,
  type TextStyle,
  type ViewStyle,
} from 'react-native';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Text } from './ui/text';
import { Button } from './ui/button';
import { Icon } from './ui/icon';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from './ui/popover';
import { Switch } from './ui/switch';
import { ToggleGroup, ToggleGroupItem } from './ui/toggle-group';
import { Row, Stack } from './layout';
import {
  Calendar,
  monthOf,
  parseIsoDate,
  todayParts,
  toIsoDate,
  type CalendarMonth,
} from './calendar';
import { useLocale } from '../providers/locale';
import { cn } from '../lib/utils';

export interface FieldProps {
  label?: string;
  hint?: string;
  error?: string | null;
  required?: boolean;
  /**
   * The control, or a function receiving the props that name and describe it.
   *
   * A function is how the control gets them: the component that renders the
   * control is the field's parent, so it cannot read them from context.
   */
  children: ReactNode | ((control: FieldControlProps) => ReactNode);
}

/**
 * What a control needs in order to be named and described by its field.
 *
 * React Native Web maps `nativeID` onto the DOM `id` and passes `aria-*`
 * through, so a plain `Text` label can name an `input` without a wrapping
 * `<label>` element — which React Native has no way to render.
 */
export interface FieldControlProps {
  nativeID?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'aria-required'?: boolean;
}


/**
 * Label, control, hint, and error as one unit.
 *
 * The label is tied to the control with `aria-labelledby`, and the hint or
 * error with `aria-describedby`, so a screen reader announces "Address, edit
 * text, Customers need an address to collect from" rather than an unnamed
 * field. A real `<label for>` would also focus the control on click, but React
 * Native Web renders `Text` as a `div` and there is no label element to point at.
 *
 * @param props Field chrome and the control.
 * @returns A labelled field.
 */
export function Field({ label, hint, error, required, children }: FieldProps) {
  // React's generated id contains punctuation, which is legal in an HTML id but
  // not in a CSS selector, so it is reduced to characters both accept.
  const base = `field-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const labelId = `${base}-label`;
  const descriptionId = `${base}-description`;
  const described = Boolean(error || hint);

  const control: FieldControlProps = {
    ...(label ? { nativeID: base, 'aria-labelledby': labelId } : {}),
    ...(described ? { 'aria-describedby': descriptionId } : {}),
    ...(error ? { 'aria-invalid': true } : {}),
    ...(required ? { 'aria-required': true } : {}),
  };

  return (
    <Stack gap="xs">
      {label ? (
        <Row gap="xs" align="start">
          {/* `min-w-0 shrink` so a label longer than the column wraps instead of
              widening the row. React Native's `flexShrink` default is 0, so
              without it the text keeps its full single-line width and paints
              over whatever sits beside it — which is what a long label did to
              the control in the next column of a two-column form. */}
          <Label nativeID={labelId} className="text-muted-foreground min-w-0 shrink font-semibold">
            {label}
          </Label>
          {/* Decorative: `aria-required` on the control is what announces it. */}
          {required ? (
            <Text aria-hidden className="text-primary text-sm font-semibold">
              *
            </Text>
          ) : null}
        </Row>
      ) : null}

      {typeof children === 'function' ? children(control) : children}

      {error ? (
        <Text nativeID={descriptionId} className="text-destructive text-xs">
          {error}
        </Text>
      ) : hint ? (
        <Text nativeID={descriptionId} className="text-subtle-foreground text-xs">
          {hint}
        </Text>
      ) : null}
    </Stack>
  );
}

export interface TextFieldProps extends Omit<TextInputProps, 'style'> {
  /** Renders the field as something the form is not asking for. */
  disabled?: boolean;
  label?: string;
  hint?: string;
  error?: string | null;
  required?: boolean;
  /** Renders a trailing unit or action inside the control. */
  suffix?: ReactNode;
  /** Shown before the input, inside the frame. */
  leading?: ReactNode;
  containerStyle?: ViewStyle;
  inputStyle?: TextStyle;
}

/**
 * A single-line or multi-line text input.
 *
 * @param props Standard `TextInput` props plus field chrome.
 * @returns A themed input.
 */
export const TextField = forwardRef<TextInput, TextFieldProps>(function TextField(
  { label, hint, error, required, leading, suffix, containerStyle, inputStyle, multiline, disabled, ...input },
  ref,
) {
  const [focused, setFocused] = useState(false);

  return (
    <Field label={label} hint={hint} error={error} required={required}>
      {(fieldControl) => (
        <View
          className={cn(
            // shadcn: `border-input` at rest, a ring while focused, and
            // `border-destructive` when invalid — the ring and the border are
            // separate signals, so a focused invalid field shows both.
            'bg-background flex-row rounded-md',
            // A field the form does not take an answer for: still readable, and
            // plainly not the thing being edited.
            disabled && 'bg-muted opacity-60',
            multiline ? 'items-start' : 'items-center',
            // Keeps the control from resizing when the focus ring thickens.
            focused ? 'border-2 px-[11px]' : 'border px-3',
            error ? 'border-destructive' : focused ? 'border-ring' : 'border-input',
            multiline ? 'min-h-20' : 'min-h-12'
          )}
          style={containerStyle}>
          {leading}
          <Input
            ref={ref}
            multiline={multiline}
            editable={!disabled}
            {...fieldControl}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            {...input}
            // React Native Web renders its own focus outline, which would double
            // up with the border above; the vendored Input turns it off already.
            className={cn(
              // Transparent in both modes: the frame above owns the field's
              // background. The vendored Input fills itself in dark mode
              // (`dark:bg-input/30`), which drew a second surface inset by the
              // frame's padding — a lighter rectangle floating inside the field.
              // `cn` merges the pair, so naming the dark case here replaces it.
              'h-auto flex-1 self-stretch border-0 bg-transparent px-0 py-2 shadow-none focus-visible:ring-0 dark:bg-transparent sm:h-auto',
              multiline && 'min-h-20'
            )}
            textAlignVertical={multiline ? 'top' : undefined}
            style={inputStyle}
          />
          {suffix ? <View className="ml-2">{suffix}</View> : null}
        </View>
      )}
    </Field>
  );
});

export interface SwitchFieldProps {
  label: string;
  description?: string;
  value: boolean;
  onValueChange: (value: boolean) => void;
  disabled?: boolean;
}

/**
 * A boolean setting with a label and explanation.
 *
 * @param props Setting state and handler.
 * @returns A switch row.
 */
export function SwitchField({ label, description, value, onValueChange, disabled }: SwitchFieldProps) {
  return (
    <Pressable
      onPress={() => !disabled && onValueChange(!value)}
      disabled={disabled}
      accessibilityRole="switch"
      aria-checked={value}
      accessibilityLabel={label}
      className={cn(disabled && 'opacity-50')}>
      <Row spread gap="lg">
        <Stack gap="xs" className="flex-1">
          <Text className="text-base font-semibold">{label}</Text>
          {description ? <Text className="text-muted-foreground text-xs">{description}</Text> : null}
        </Stack>
        {/* The whole row is the tap target, so the switch itself must not also
            handle the press: on the web its click would bubble to the row and
            toggle the value twice. Hidden from assistive technology for the same
            reason it takes no press: the row is the control, and a second
            unnamed switch beside it is one announcement too many. */}
        <View pointerEvents="none" aria-hidden>
          <Switch checked={value} onCheckedChange={onValueChange} disabled={disabled} />
        </View>
      </Row>
    </Pressable>
  );
}

/**
 * A yes/no choice rendered as two adjacent buttons.
 *
 * Clearer than a switch when the two options are not obviously on/off, such as
 * choosing between "As soon as possible" and "Schedule".
 *
 * The vendored `RadioGroup` draws a 16-point dot with an indicator, while each
 * option here is a full-width card that carries its own label and description,
 * so the options are pressables with the radio role and state instead.
 *
 * @param props The two options and the current selection.
 * @returns The choice row.
 */
/**
 * A dropdown for choosing one value from a list too long for a row of buttons.
 *
 * Distinct from {@link ChoiceField}, which renders every option inline and is
 * only usable for two or three.
 *
 * @param props The options, the current value, a change handler, and a label.
 * @returns The dropdown.
 */
export function SelectField<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  /** Choices, each with the text to show for it. */
  options: Array<{ value: T; label: string }>;
  value: T;
  onChange: (value: T) => void;
  label: string;
}) {
  const selected = options.find((option) => option.value === value);
  return (
    <Select
      value={{ value, label: selected?.label ?? value }}
      onValueChange={(option) => {
        if (option?.value) onChange(option.value as T);
      }}>
      <SelectTrigger accessibilityLabel={label} className="w-full">
        <SelectValue placeholder={selected?.label ?? value} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value} label={option.label}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function ChoiceField<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  /**
   * Choices, each with the text to show for it.
   *
   * `disabled` is for a choice the screen can already know is impossible — a
   * shut restaurant takes no ASAP order — so the reason sits on the card rather
   * than arriving afterwards as a refusal from the button below it.
   */
  options: Array<{ value: T; label: string; description?: string; disabled?: boolean }>;
  value: T;
  onChange: (value: T) => void;
  label?: string;
}) {
  return (
    <Field label={label}>
      <Row gap="sm" align="stretch">
        {options.map((option) => {
          const selected = option.value === value;
          const disabled = Boolean(option.disabled);
          return (
            <Pressable
              key={option.value}
              onPress={() => onChange(option.value)}
              disabled={disabled}
              accessibilityRole="radio"
              // `aria-checked`, not `accessibilityState`. On web the state has
              // to reach the DOM as ARIA, and react-native-web reads `aria-*`
              // props rather than the state object — which is why this pair
              // carried neither before, leaving the chosen mode to colour
              // alone. A radio's state is `checked`.
              aria-checked={selected}
              accessibilityLabel={option.label}
              className={cn(
                'min-h-12 flex-1 justify-center rounded-md px-3 py-2',
                selected ? 'border-primary bg-primary-soft border-2' : 'border-input bg-card border',
                disabled && 'opacity-50'
              )}>
              <Text
                className={cn(
                  'text-base font-semibold',
                  selected ? 'text-primary-strong' : 'text-foreground'
                )}>
                {option.label}
              </Text>
              {option.description ? (
                <Text className="text-muted-foreground text-xs">{option.description}</Text>
              ) : null}
            </Pressable>
          );
        })}
      </Row>
    </Field>
  );
}

/** Two digits, as every time value in the app is written. */
const pad2 = (value: number) => String(value).padStart(2, '0');

/**
 * Reads an `HH:MM` value.
 *
 * @param value The form's value.
 * @returns The hour and minute, or null when it is not a time.
 */
export function parseTimeOfDay(value: string): { hour: number; minute: number } | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  // `24:00` is midnight at the end of the day, which a field that closes shows
  // as hour `24`. The value it holds for that is `00:00`, so this is normalised
  // rather than carried as a second spelling the picker would have to know.
  if (hour === 24 && minute === 0) return { hour: 0, minute: 0 };
  if (hour > 23 || minute > 59) return null;
  return { hour, minute };
}

/**
 * Writes an `HH:MM` value.
 *
 * @param hour 0–23.
 * @param minute 0–59.
 * @returns The time as the form holds it.
 */
export function formatTimeOfDay(hour: number, minute: number): string {
  return `${pad2(hour)}:${pad2(minute)}`;
}

/**
 * The hour and minute dropdowns both time pickers share.
 *
 * A visible label on each: `SelectField`'s label is only its accessible name, so
 * the pair would otherwise read as two unlabelled numbers. The minute list is
 * five-minute steps plus whatever the value already holds — as fine as a serving
 * window or an offer needs, without snapping a time of 03:07.
 *
 * @param props The current time and the change handler.
 * @returns The two dropdowns.
 */
export function TimeSelects({
  hour,
  minute,
  onChange,
  allowEndOfDay = false,
}: {
  hour: number;
  minute: number;
  onChange: (hour: number, minute: number) => void;
  /**
   * Offers `24` as an hour, for a field whose time closes a day.
   *
   * Midnight is stored — the model holds minutes from midnight in one range, so
   * `00:00` is the only spelling it has — but as a *closing* time `00:00` reads
   * as the start of the following day, which is the opposite of what is meant.
   * The 24-hour clock already names that moment `24:00`, so the picker offers it
   * rather than making the merchant translate.
   */
  allowEndOfDay?: boolean;
}) {
  const { t } = useLocale();

  // Whether the merchant has said "the end of the day" during this visit to the
  // picker. A stored `00:00` is ambiguous — midnight opening a day and midnight
  // closing one are the same value — so it is read as the end of the day until
  // they say otherwise, and remembered from then on.
  //
  // Without this, choosing hour `00` snapped straight back to `24` and locked
  // the minutes, so `00:00 – 00:30` could not be entered at all: there was no
  // way to hold hour zero long enough to give it a minute.
  const [endOfDay, setEndOfDay] = useState<boolean | null>(null);
  const atEndOfDay = allowEndOfDay && (endOfDay ?? (hour === 0 && minute === 0));

  // `24` is only ever `24:00`: the end of a day is not a moment with minutes.
  const hours = Array.from({ length: allowEndOfDay ? 25 : 24 }, (_, value) => ({
    value: pad2(value),
    label: pad2(value),
  }));

  // Locked to the hour rather than disabled: the list holds the one value that
  // can follow `24`, so the control still shows what it is set to. `00:30` is
  // reached by choosing hour `00` first, which leaves the minutes free.
  const minuteValues = atEndOfDay ? [0] : Array.from({ length: 12 }, (_, index) => index * 5);
  if (!atEndOfDay && !minuteValues.includes(minute)) minuteValues.push(minute);
  const minutes = minuteValues.sort((a, b) => a - b).map((value) => ({
    value: pad2(value),
    label: pad2(value),
  }));

  return (
    <Row gap="sm">
      <View className="flex-1">
        <Stack gap="xs">
          <Text className="text-muted-foreground text-xs font-semibold">{t('common.hour')}</Text>
          <SelectField
            label={t('common.hour')}
            options={hours}
            value={atEndOfDay ? '24' : pad2(hour)}
            onChange={(value) => {
              const next = Number(value);
              setEndOfDay(next === 24);
              // `24` stores midnight and drops the minutes it cannot carry;
              // every other hour keeps them, so leaving `24` does not lose what
              // was typed and `00:30` is one more tap.
              onChange(next === 24 ? 0 : next, next === 24 ? 0 : minute);
            }}
          />
        </Stack>
      </View>
      <View className="flex-1">
        <Stack gap="xs">
          <Text className="text-muted-foreground text-xs font-semibold">{t('common.minute')}</Text>
          <SelectField
            label={t('common.minute')}
            options={minutes}
            value={pad2(minute)}
            onChange={(value) => onChange(hour, Number(value))}
          />
        </Stack>
      </View>
    </Row>
  );
}

/**
 * A time, picked rather than typed.
 *
 * It used to be a text box wanting `HH:MM`, with a hint naming the format. The
 * hint was the tell: a format to remember is a format to get wrong. This opens
 * the same hour and minute dropdowns the date-time field uses, so the two
 * controls in the app that ask for a time ask for it the same way.
 *
 * @param props Field props; `value` is `HH:MM`.
 * @returns The time field.
 */
export function TimeField({
  label,
  value,
  onChange,
  error,
  disabled,
  hint,
  midnightMeansEndOfDay = false,
}: {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  error?: string | null;
  disabled?: boolean;
  hint?: string;
  /**
   * Reads a stored `00:00` as the end of the day and shows it as `24:00`.
   *
   * For a field that means "until", where `00:00` is how the 24-hour clock
   * spells midnight at the *end* of the day. Left alone it is a time on the
   * clock, which is what the field shows for an opening time — where `00:00` is
   * the start of the day and `24:00` would be wrong.
   *
   * The stored value does not change: `24:00` is not a time the model holds, and
   * everything downstream reads minutes from midnight in a single 0–1439 range.
   * This is the field agreeing with the summary line above it, which spells the
   * same value `24:00` because it reaches the customer that way.
   */
  midnightMeansEndOfDay?: boolean;
}) {
  const { t } = useLocale();
  const parsed = parseTimeOfDay(value);
  const time = parsed ?? { hour: 9, minute: 0 };
  const atEndOfDay = midnightMeansEndOfDay && time.hour === 0 && time.minute === 0;

  return (
    <Field label={label} error={error} hint={hint}>
      {(fieldControl) => (
        <Popover>
          <PopoverTrigger asChild>
            <Pressable
              {...fieldControl}
              accessibilityRole="button"
              disabled={disabled}
              className={cn(
                'bg-background border-input min-h-12 flex-row items-center justify-between gap-2 rounded-md border px-3',
                disabled && 'bg-muted opacity-60',
                error && 'border-destructive'
              )}>
              <Text
                className={cn('text-sm', parsed ? 'text-foreground' : 'text-subtle-foreground')}>
                {parsed
                  ? atEndOfDay
                    ? '24:00'
                    : formatTimeOfDay(time.hour, time.minute)
                  : t('common.time')}
              </Text>
              <Icon as={Clock} size={16} className="text-muted-foreground" />
            </Pressable>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[240px] p-3">
            <Stack gap="sm">
              <TimeSelects
                hour={time.hour}
                minute={time.minute}
                allowEndOfDay={midnightMeansEndOfDay}
                onChange={(hour, minute) => onChange(formatTimeOfDay(hour, minute))}
              />
              <Row justify="end" className="border-border border-t pt-2">
                <PopoverClose
                  accessibilityRole="button"
                  accessibilityLabel={t('common.done')}
                  className="bg-primary h-9 items-center justify-center rounded-md px-3 active:opacity-90">
                  <Text className="text-primary-foreground text-sm font-medium">
                    {t('common.done')}
                  </Text>
                </PopoverClose>
              </Row>
            </Stack>
          </PopoverContent>
        </Popover>
      )}
    </Field>
  );
}

/**
 * A date, picked rather than typed.
 *
 * It used to be a text box wanting `YYYY-MM-DD`, a format to remember. This is
 * the same month grid the date-and-time field opens, without the time.
 *
 * @param props Field props; `value` is `YYYY-MM-DD`.
 * @returns The date field.
 */
export function DateField({
  label,
  value,
  onChange,
  error,
  disabled,
  hint,
}: {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  error?: string | null;
  disabled?: boolean;
  hint?: string;
}) {
  const { t, locale } = useLocale();
  const parsed = parseIsoDate(value);
  // Seeded from the value so opening the field shows its own month.
  const [view, setView] = useState<CalendarMonth>(() => monthOf(value) ?? todayParts());

  const display = parsed
    ? new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(
        new Date(parsed.year, parsed.month, parsed.day)
      )
    : t('common.date');

  const goToday = () => {
    const now = todayParts();
    setView({ year: now.year, month: now.month });
    onChange(toIsoDate(now.year, now.month, now.day));
  };

  return (
    <Field label={label} error={error} hint={hint}>
      {(fieldControl) => (
        <Popover>
          <PopoverTrigger asChild>
            <Pressable
              {...fieldControl}
              accessibilityRole="button"
              disabled={disabled}
              className={cn(
                'bg-background border-input min-h-12 flex-row items-center justify-between gap-2 rounded-md border px-3',
                disabled && 'bg-muted opacity-60',
                error && 'border-destructive'
              )}>
              <Text
                className={cn('text-sm', parsed ? 'text-foreground' : 'text-subtle-foreground')}>
                {display}
              </Text>
              <Icon as={CalendarDays} size={16} className="text-muted-foreground" />
            </Pressable>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[300px] p-3">
            <Stack gap="sm">
              <Calendar value={value} month={view} onMonthChange={setView} onChange={onChange} />

              <Row spread align="center" className="border-border border-t pt-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onPress={goToday}
                  accessibilityLabel={t('common.today')}>
                  <Text>{t('common.today')}</Text>
                </Button>
                <PopoverClose
                  accessibilityRole="button"
                  accessibilityLabel={t('common.done')}
                  className="bg-primary h-9 items-center justify-center rounded-md px-3 active:opacity-90">
                  <Text className="text-primary-foreground text-sm font-medium">
                    {t('common.done')}
                  </Text>
                </PopoverClose>
              </Row>
            </Stack>
          </PopoverContent>
        </Popover>
      )}
    </Field>
  );
}

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  /** Optional count badge, for example the number of orders in a tab. */
  badge?: number;
  /**
   * Optional second label, drawn in the destructive tone.
   *
   * The orders board uses it for the orders *inside* a tab that are already
   * late: those cannot be seen from another tab, so the one number that has to
   * travel with the tab is how much of it is behind. A string rather than a
   * count, so the caller keeps the plural rule and the wording.
   */
  hint?: string;
}

/** Square tap target per stepper size; the `sm:` pair cancels the primitive's. */
const STEPPER_BUTTON = {
  sm: 'h-8 w-8 sm:h-8 sm:w-8',
  md: 'h-11 w-11 sm:h-11 sm:w-11',
} as const;

/** Minimum width of the readout, so the row does not shift as digits change. */
const STEPPER_VALUE = {
  sm: 'min-w-6',
  md: 'min-w-8',
} as const;

/**
 * How long the chosen chip takes to travel back into view, in milliseconds.
 *
 * >> Tuning the tab bar's glide means changing this and `glideEase` below, and
 * >> nothing else. This is the whole journey's duration whatever the distance —
 * >> lower is faster, higher is slower — and that one is its shape.
 *
 * Written by hand rather than left to `scroll-behavior: smooth`, whose duration
 * grows with the distance: measured at ~1.35ms per pixel, so ~80ms for a row of
 * short names and ~400ms for a row of long ones, which read as sluggish.
 */
const STRIP_GLIDE_MS = 600;

/**
 * The shape of that journey: a decelerating cubic, so the row covers most of the
 * distance early and spends the rest settling into place. Change it alongside
 * `STRIP_GLIDE_MS` above.
 */
const glideEase = (t: number) => 1 - (1 - t) ** 3;

/**
 * A tab strip.
 *
 * Scrolls horizontally because the merchant dashboard has ranges that will not
 * fit on a phone, and wrapping tabs look like a layout bug.
 *
 * @param props Options, current value, and change handler.
 * @returns The scrollable tab strip.
 */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  fullWidth,
  disabled,
  variant = 'tray',
}: {
  options: Array<SegmentedOption<T>>;
  value: T;
  onChange: (value: T) => void;
  fullWidth?: boolean;
  /**
   * How the chosen item is marked.
   *
   * `tray` is the original: a grey tray with the chosen item as a raised card.
   * `brand` drops the tray and fills the chosen item with the brand colour — the
   * shape the menu's category row wants, where the row is a jump list rather
   * than a filter. Kept apart because the merchant's queue filters are the
   * other caller, and a look that suits one is not automatically right for it.
   */
  variant?: 'tray' | 'brand';
  /**
   * When there is nothing to choose between.
   *
   * Without this the control accepts a press, reports the new value, and the
   * caller recomputes the same one from a rule — so it looks responsive and
   * snaps back, which reads as a fault rather than a constraint.
   */
  disabled?: boolean;
}) {
  /**
   * The chosen chip, so it can be brought back into view.
   *
   * On a long menu this control is a jump list rather than a pair of segments,
   * and the chip that says where the customer is can be scrolled off the side.
   * Left there, the control only says where they last tapped.
   */
  const brand = variant === 'brand';
  const chosen = useRef<ComponentRef<typeof ToggleGroupItem> | null>(null);
  /**
   * Whether this strip has been put in place yet.
   *
   * The first placement snaps and every one after it glides — see
   * `keepInView` below for why an arrival is not something to animate.
   */
  const positioned = useRef(false);

  // A layout effect, not a passive one: this reads geometry and writes a scroll
  // position, and both belong to the frame being assembled. Passive, the row can
  // be painted once at the offset it mounted with — scrollLeft 0, which on a
  // mount that opens mid-list leaves the marked chip off the right-hand edge —
  // before the snap below has moved it.
  useLayoutEffect(() => {
    if (typeof document === 'undefined') return;
    const node = chosen.current as unknown as HTMLElement | null;
    if (!node) return;

    // The element that actually scrolls sideways, found by walking up rather
    // than through a ref: the row's ref is not the element carrying the
    // overflow.
    let bar: HTMLElement | null = node.parentElement;
    while (bar && bar.scrollWidth <= bar.clientWidth + 1) bar = bar.parentElement;
    if (!bar) return;

    /**
     * Puts the chosen chip where it belongs.
     *
     * Measured against the row's own box, not the window's. They are the same
     * today — the row spans the screen — but they are not the same thing, and a
     * chip can sit inside the window and outside the row, where a window-shaped
     * test would never look for it.
     *
     * The offset is computed and clamped rather than left to `scrollIntoView`,
     * which cannot centre the first chip — that needs a negative scroll — and
     * settles for pushing the content box to the edge instead, one padding
     * short. It is written to `scrollLeft` rather than passed to `scrollTo`,
     * which react-native-web's scroll view host ignores, and the travel is
     * animated by hand so its duration is the row's to choose. Left to right
     * throughout: an RTL locale would need the ends swapped.
     */
    let glide = 0;
    const keepInView = () => {
      const box = node.getBoundingClientRect();
      const barBox = bar.getBoundingClientRect();
      const shift = bar.scrollLeft + (box.left - barBox.left);
      // Centred, unless the chip is wider than the row — centring that hangs
      // half the overflow off each side and cuts the start of the label, which
      // is the half a customer reads. Too wide to fit is aligned to its leading
      // edge instead, so a long name begins where it should.
      const wanted =
        box.width >= bar.clientWidth - 1
          ? shift
          : shift - (bar.clientWidth - box.width) / 2;
      const target = Math.max(0, Math.min(wanted, bar.scrollWidth - bar.clientWidth));

      // Only when it is somewhere else. Re-issuing a scroll that is already
      // running restarts it, and during a scroll of the list that is a stutter
      // rather than a correction — which is what made this feel worse than
      // leaving the row alone.
      //
      // The first positioning after mount is not a correction at all: the strip
      // is being put where it already belongs, and the customer has changed
      // nothing to animate. Gliding it there is an arrival dance nobody asked
      // for, visible whenever a screen restores a position that leaves this row
      // on screen — the menu reopening at its foot, for one. Later moves are
      // changes to react to, and those glide.
      const first = !positioned.current;
      positioned.current = true;
      if (Math.abs(target - bar.scrollLeft) <= 1) return;

      const still =
        first ||
        (typeof matchMedia === 'function' &&
          matchMedia('(prefers-reduced-motion: reduce)').matches);
      if (still) {
        bar.scrollLeft = target;
        return;
      }

      // Animated here rather than by `scroll-behavior: smooth`, so the time is
      // the row's to choose and does not stretch with the distance.
      cancelAnimationFrame(glide);
      const from = bar.scrollLeft;
      const started = performance.now();
      const step = (now: number) => {
        const t = Math.min(1, (now - started) / STRIP_GLIDE_MS);
        bar.scrollLeft = from + (target - from) * glideEase(t);
        if (t < 1) glide = requestAnimationFrame(step);
      };
      glide = requestAnimationFrame(step);
    };

    keepInView();

    // Geometry moves without the selection moving: a rotation, a window resize,
    // the language switch rebuilding the labels at different widths, a font
    // arriving after first paint. None of those change `value`, so none of them
    // would have re-run this.
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(keepInView);
    observer.observe(bar);
    if (bar.firstElementChild) observer.observe(bar.firstElementChild);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(glide);
    };
  }, [value]);

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      // The content wrapper shrink-wraps the strip, so a `w-full` inside it
      // resolves against the strip's own width and stretches nothing. Giving
      // the wrapper the scroll view's own width is what lets `fullWidth` mean
      // full width when the options are narrower than the space they sit in.
      contentContainerClassName={cn('gap-2 py-0.5', fullWidth && 'min-w-full')}>
      <ToggleGroup
        type="single"
        value={value}
        // The primitive reports `undefined` when the selected tab is pressed
        // again; a tab strip always has a selection, so that press is ignored.
        onValueChange={(next) => {
          if (!disabled && next !== undefined) onChange(next as T);
        }}
        className={cn(
          'flex-row items-center gap-1',
          !brand && 'bg-muted rounded-md p-[3px]',
          fullWidth ? 'w-full min-w-full' : 'self-start',
          disabled && 'opacity-50'
        )}>
        {options.map((option) => {
          const selected = option.value === value;
          return (
            <ToggleGroupItem
              key={option.value}
              ref={selected ? chosen : undefined}
              value={option.value}
              role="tab"
              disabled={disabled}
              aria-selected={selected}
              className={cn(
                'h-[38px] flex-row items-center justify-center gap-1 rounded-sm px-3 py-2 sm:h-[38px] sm:min-w-0 sm:px-3',
                fullWidth ? 'flex-1' : 'flex-none',
                brand
                  ? // The fill marks the choice, not the label: the primitive
                    // draws its own tint here, which is the grey block to lose.
                    selected && 'bg-primary'
                  : selected
                    ? 'bg-card'
                    : 'bg-transparent'
              )}>
              <Text
                numberOfLines={1}
                className={cn(
                  'text-sm',
                  brand
                    ? // White on the fill, grey beside it: the colour is the block's.
                      selected
                      ? 'text-primary-foreground font-bold'
                      : 'text-muted-foreground font-medium'
                    : cn('font-semibold', selected ? 'text-foreground' : 'text-muted-foreground')
                )}>
                {option.label}
              </Text>
              {option.badge !== undefined && option.badge > 0 ? (
                <View
                  className={cn(
                    'min-w-5 items-center rounded-full px-1.5 py-px',
                    selected ? 'bg-primary' : 'bg-input'
                  )}>
                  <Text
                    className={cn(
                      'text-[11px] font-bold',
                      selected ? 'text-primary-foreground' : 'text-muted-foreground'
                    )}>
                    {option.badge}
                  </Text>
                </View>
              ) : null}
              {/* The dot as well as the words: the count is what matters, and a
                  colour on its own is not a count. */}
              {option.hint !== undefined ? (
                <View className="flex-row items-center gap-1">
                  <View className="bg-destructive h-1.5 w-1.5 rounded-full" />
                  <Text className="text-destructive text-[11px] font-bold">{option.hint}</Text>
                </View>
              ) : null}
            </ToggleGroupItem>
          );
        })}
      </ToggleGroup>
    </ScrollView>
  );
}

/**
 * A quantity stepper with large tap targets.
 *
 * @param props Current quantity and change handler.
 * @returns The stepper row.
 */
export function Stepper({
  value,
  onChange,
  min = 0,
  max = 99,
  floor = min + 1,
  size = 'md',
  disabled,
  what,
}: {
  value: number;
  onChange: (value: number) => void;
  /** The value a line drops to when it is emptied. */
  min?: number;
  max?: number;
  /**
   * The smallest non-zero quantity. Decrementing at the floor clears the line
   * rather than stepping below it, which is what a dish sold in fives needs:
   * its quantities are 5, 6, 7 … and 0, never 1 to 4.
   */
  floor?: number;
  size?: 'sm' | 'md';
  disabled?: boolean;
  /**
   * What the number counts, for the buttons' accessible names.
   *
   * A page with four steppers on it otherwise offers a screen reader eight
   * buttons called "Increase quantity" and "Decrease quantity", and the only way
   * to tell which is which is to count them — the label beside the number is a
   * sibling, not part of the control. Naming the thing keeps the two buttons
   * distinct and says what moving them changes.
   */
  what?: string;
}) {
  const button = (label: string, next: number, labelText: string, isDisabled: boolean) => (
    <Pressable
      onPress={() => {
        if (!isDisabled) onChange(next);
      }}
      disabled={isDisabled}
      accessibilityRole="button"
      accessibilityLabel={labelText}
      className={cn(
        STEPPER_BUTTON[size],
        'shrink-0 items-center justify-center rounded-sm',
        isDisabled ? 'bg-muted opacity-50' : 'bg-primary-soft'
      )}>
      <Text className="text-primary-strong text-lg font-bold">{label}</Text>
    </Pressable>
  );

  const increaseLabel = what ? `Increase ${what}` : 'Increase quantity';
  const decreaseLabel = what ? `Decrease ${what}` : 'Decrease quantity';

  return (
    <Row gap="sm" align="center">
      {button('−', value > floor ? value - 1 : min, decreaseLabel, disabled || value <= min)}
      <Text className={cn('text-center text-base font-bold', STEPPER_VALUE[size])}>{value}</Text>
      {button('+', value + 1, increaseLabel, disabled || value >= max)}
    </Row>
  );
}
